import React, { useState, useRef } from 'react';
import { base44 } from "@/api/base44Client";
import { useQuery } from '@tanstack/react-query';
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Handshake, Search, X, FileText, ExternalLink, AlertTriangle, ChevronRight, Video, RefreshCw, UserX, LogIn, Users, BarChart3 } from "lucide-react";
import { useAuth } from '@/lib/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";
import { toast } from "sonner";
import HomeHeader from "@/components/home/HomeHeader";
import StatSquares from "@/components/home/StatSquares";
import ContactRow from "@/components/home/ContactRow";
import { useSelectedShow, ALL_SHOWS } from "@/hooks/useSelectedShow";
import { useOutboxStatus } from "@/hooks/useOutboxStatus";
import { getSummary } from "@/lib/offlineOutbox";
import { needsTyping } from "@/lib/contactStatus";
import PageContainer from '@/components/PageContainer';
import { openExternalLink } from '@/lib/platform';

/**
 * Sentinel for "All reps" in the filter-by-rep dropdown below, same reason
 * useSelectedShow.jsx's own ALL_SHOWS exists: Radix Select cannot use an
 * empty string as an item value, so this stands in for "no rep selected"
 * on the wire while the actual filter state stays a plain '' meaning "off".
 */
const ALL_REPS = '__all_reps__';

/**
 * Shared across the whole team, not per rep, task: "Run a Score Card"
 * button (2026-09-24, moved here from the Shows list screen the same day
 * after the owner saw Home is actually where a rep wants this). Hardcoded
 * on purpose, same as the owner's office address in messages.ts: this is
 * the one team-wide Score Card link every rep uses today, not a per-rep
 * field, so there is nothing to store in the database for it.
 */
const SCORECARD_URL = 'https://scorecard.realtimefeedback.com/c/adam?code=GFR-34A-ZWE';

/**
 * Which of the four squares a contact belongs to, task: Home screen
 * redesign (2026-09-08). Real, computed definitions, not the old
 * pending/accepted/declined status column, which described a confirmed
 * meeting flow that never ran in production (CLAUDE.md sections 5/6):
 *
 *   booked   meeting_booked = 1, the Zoom Scheduler webhook's own signal
 *   sent     at least one channel has gone out, not booked
 *   pending  neither channel has gone out yet, not booked
 *
 * Booked wins over sent/pending regardless of send history: once a meeting
 * is booked, whether a reminder happened to go out first does not matter.
 */
function bucketOf(meeting) {
  if (meeting.meeting_booked) return 'booked';
  if (meeting.sms_sent_at || meeting.email_sent_at) return 'sent';
  return 'pending';
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('captured');
  const [searchQuery, setSearchQuery] = useState('');
  // Task: filter by rep, 2026-09-17. '' means off (every rep), same
  // convention filter/searchQuery already use.
  const [repFilter, setRepFilter] = useState('');
  const resultsRef = useRef(null);
  const { selectedShowId, setSelectedShowId, isAllShows, selectedShow, isLoading: isLoadingShows } = useSelectedShow();

  // Every active, non-device rep, company wide, task: filter by rep,
  // 2026-09-17. The same GET /api/reps NewMeeting.jsx's own "Scanning As"
  // picker already uses (worker/routes/reps.ts, is_device_account = 0 at
  // the SQL level, so a shared booth login can never appear here either),
  // called with no show_id so this always offers every rep regardless of
  // which show Home itself is currently showing: Home can show "All
  // shows" contacts at once, and narrowing the rep list to whichever show
  // happens to be selected would make the dropdown's own options shift
  // under a rep's thumb for no reason tied to what they are trying to do.
  const { data: reps = [] } = useQuery({
    queryKey: ['home-reps'],
    queryFn: () => base44.reps.list(),
  });

  const { total: pendingSyncCount, stuck: stuckSyncCount, authExpired: authExpiredCount, flushNow } = useOutboxStatus();

  // Three real, mutually distinct reasons a queued capture has not synced
  // yet (task: stuck captures, 2026-09-08), not one flat "waiting" state:
  //
  //   stuck        the server rejected the same payload MAX_SERVER_ATTEMPTS
  //                times in a row. Retrying again will not help; a human has
  //                to look at it, so this navigates to the Stuck Captures
  //                screen instead of blindly retrying a fourth time.
  //   authExpired  the rep's session died mid-sweep. The record itself is
  //                fine, nothing to fix about the data, so this navigates to
  //                /login rather than the diagnostic screen: logging back in
  //                is the whole fix, and the very next automatic flush
  //                (OutboxSync mounts fresh on that reload) picks it up with
  //                no further tap needed.
  //   otherwise    a plain "no signal yet" wait. Tapping retries right now
  //                and reports what actually happened, rather than retrying
  //                silently the way this used to.
  const handleSyncBannerTap = async () => {
    if (stuckSyncCount > 0) {
      navigate('/stuck-captures');
      return;
    }
    if (authExpiredCount > 0) {
      navigate('/login');
      return;
    }
    const before = await getSummary();
    await flushNow();
    const after = await getSummary();
    if (after.total === 0) {
      toast.success('All caught up. Everything synced.');
    } else if (after.total < before.total) {
      toast.success(`Synced ${before.total - after.total} of ${before.total}. ${after.total} still waiting.`);
    } else if (after.stuck > before.stuck || after.authExpired > before.authExpired) {
      toast.error('Could not sync. Tap again to see what is wrong.');
    } else {
      toast('Still offline. Will keep trying automatically.');
    }
  };

  // Never shown for a device account (task: simplify the device account
  // experience, follow-up, 2026-09-16): a shared booth login never connects
  // its own Google account, by design, an email for a contact it captures
  // sends using whichever rep is credited on "Scanning As"
  // (worker/lib/outboundSend.ts's `contact.scanned_by_user_id ?? callerId`),
  // never the caller's own connection. Before this, the banner showed
  // unconditionally and permanently while signed in as a device account,
  // since its own google_status can never be anything but 'disconnected',
  // and it was also a dead end: it links to /profile, which for a device
  // account now shows nothing but its login email, nothing to reconnect.
  const googleWarning =
    user?.is_device_account
      ? null
      : user?.google_status === 'error'
        ? 'Google connection needs to be reconnected.'
        : user?.google_status === 'disconnected'
          ? "Google not connected. Emails won't send."
          : null;

  const { data: meetings = [], isLoading } = useQuery({
    queryKey: ['meetings'],
    queryFn: async () => {
      const all = await base44.entities.MeetingRequest.list('-created_date', 200);
      return all.filter(m => m.status !== 'deleted');
    },
  });

  const showFiltered = isAllShows
    ? meetings
    : selectedShowId
      ? meetings.filter(m => m.trade_show_id === selectedShowId)
      : [];

  // Task: filter by rep, 2026-09-17. Client side, same as every other
  // filter on this page (show, search, the four stat buckets): this page
  // already loads all 200 most recent contacts once and slices them with
  // plain array filters rather than a query param per filter, so this
  // follows the same pattern instead of introducing the only server side
  // one. Sits between the show filter and search, ahead of the stat tile
  // counts below, so picking a rep narrows Captured/Pending/Sent/Booked
  // the same way picking a show already does.
  const repFiltered = repFilter
    ? showFiltered.filter(m => m.scanned_by_user_id === repFilter)
    : showFiltered;

  const searchFiltered = searchQuery.trim()
    ? repFiltered.filter(m => {
        const q = searchQuery.toLowerCase();
        return (
          `${m.first_name} ${m.last_name}`.toLowerCase().includes(q) ||
          (m.company || '').toLowerCase().includes(q) ||
          (m.email || '').toLowerCase().includes(q) ||
          (m.phone || '').toLowerCase().includes(q)
        );
      })
    : repFiltered;

  // Orthogonal to booked/sent/pending, not a fourth bucket: a booked or
  // already-sent contact can just as easily need a name (offline, no OCR
  // yet, or OCR ran and found nothing), so this is its own filter value
  // rather than folded into bucketOf. Only the actionable subset: a
  // capture still waiting on the deferred OCR pass (badgeStatus 'pending')
  // is not included, since there is nothing for a rep to do about that one
  // yet, and it resolves itself within seconds of syncing.
  const filtered = filter === 'captured'
    ? searchFiltered
    : filter === 'needs_typing'
      ? searchFiltered.filter(needsTyping)
      : searchFiltered.filter(m => bucketOf(m) === filter);

  const counts = {
    captured: searchFiltered.length,
    pending: searchFiltered.filter(m => bucketOf(m) === 'pending').length,
    sent: searchFiltered.filter(m => bucketOf(m) === 'sent').length,
    booked: searchFiltered.filter(m => bucketOf(m) === 'booked').length,
  };

  // Global, like the sync-pending count above: whether a contact needs
  // typing does not depend on which show is currently selected.
  const needsTypingCount = meetings.filter(needsTyping).length;

  return (
    <div className="min-h-screen bg-background safe-top pb-24">
      {pendingSyncCount > 0 && (
        // min-h-12 (48px), not just vertical padding around one line of
        // text: padding alone shrinks back under the ~44px minimum tap
        // target the moment this wraps to a longer count, and a fixed
        // minimum guarantees the whole bar clears it regardless. No
        // trailing chevron: this used to have one implying "more detail on
        // the next screen", which is not what tapping it actually does
        // (an immediate, in-place retry, no navigation) and was flagged as
        // ambiguous for exactly that reason.
        <button
          type="button"
          onClick={handleSyncBannerTap}
          className={`flex w-full items-center gap-2 min-h-12 px-4 text-left transition-colors text-white ${
            stuckSyncCount > 0
              ? 'bg-red-600 hover:bg-red-700'
              : authExpiredCount > 0
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-slate-700 hover:bg-slate-800'
          }`}
        >
          <PageContainer className="w-full flex items-center gap-2">
            {stuckSyncCount > 0 ? (
              <AlertTriangle className="w-4 h-4 shrink-0" />
            ) : authExpiredCount > 0 ? (
              <LogIn className="w-4 h-4 shrink-0" />
            ) : (
              <RefreshCw className="w-4 h-4 shrink-0" />
            )}
            <span className="flex-1 text-sm font-medium">
              {stuckSyncCount > 0
                ? `${stuckSyncCount} contact${stuckSyncCount === 1 ? '' : 's'} could not sync. Tap to review.`
                : authExpiredCount > 0
                  ? `${authExpiredCount} contact${authExpiredCount === 1 ? '' : 's'} need you to log in again to sync. Tap to log in.`
                  : `${pendingSyncCount} contact${pendingSyncCount === 1 ? '' : 's'} waiting to sync.`}
            </span>
          </PageContainer>
        </button>
      )}
      {needsTypingCount > 0 && (
        // Bug fixed 2026-09-24: needsTypingCount above is global, every show
        // at once (its own comment says so), but the list this reveals was
        // scoped to whichever show happened to be selected, persisted in
        // localStorage from a previous visit (useSelectedShow.jsx). Tapping
        // this only ever changed the filter dimension, never the show, so a
        // stuck contact outside the currently selected show, a different
        // show entirely, or the personal/general bucket, resolved to zero
        // matches: the generic "No contacts yet" empty state, indistinguishable
        // from the tap doing nothing at all. Switches to All Shows first, not
        // to the specific show the stuck contact happens to belong to: a rep
        // can accumulate more than one of these across different shows over
        // time, and All Shows is the one selection that surfaces all of them
        // regardless of which show is currently pinned.
        <button
          type="button"
          onClick={() => {
            setSelectedShowId(ALL_SHOWS);
            setFilter('needs_typing');
          }}
          className="block w-full text-left transition-colors bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          <PageContainer className="px-4 py-2.5 flex items-center gap-2">
            <UserX className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-sm font-medium">
              {needsTypingCount} contact{needsTypingCount === 1 ? '' : 's'} need{needsTypingCount === 1 ? 's' : ''} details typed in.
            </span>
            <ChevronRight className="w-4 h-4 shrink-0" />
          </PageContainer>
        </button>
      )}
      {googleWarning && (
        <Link to="/profile" className="block bg-amber-500 hover:bg-amber-600 text-white transition-colors">
          <PageContainer className="px-4 py-2.5 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-sm font-medium">{googleWarning}</span>
            <ChevronRight className="w-4 h-4 shrink-0" />
          </PageContainer>
        </Link>
      )}
      <PageContainer className="px-4 py-6">
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}>
          <HomeHeader />
        </motion.div>

        {!isAllShows && selectedShow?.doc_url && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.033 }} className="mb-4">
            <a
              href={selectedShow.doc_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-4 py-2.5 bg-card border border-border rounded-xl hover:bg-secondary/40 transition-colors"
            >
              <FileText className="w-4 h-4 text-accent shrink-0" />
              <span className="text-sm font-medium text-foreground truncate">{selectedShow.doc_name || 'Show document'}</span>
              <ExternalLink className="w-3.5 h-3.5 text-muted-foreground ml-auto shrink-0" />
            </a>
          </motion.div>
        )}

        {user?.scheduler_link && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.038 }} className="mb-4">
            <a
              href={user.scheduler_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-4 py-2.5 bg-card border border-border rounded-xl hover:bg-secondary/40 transition-colors"
            >
              <Video className="w-4 h-4 text-blue-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground font-medium">Scheduler</p>
                <p className="text-sm font-semibold text-foreground">Book 15, 30 or 60 min ↗</p>
              </div>
            </a>
          </motion.div>
        )}

        {/* Task: "Run a Score Card" button (2026-09-24). A real <button>,
            not an <a> like the two cards above it: openExternalLink
            (web/src/lib/platform.js) has to run through @capacitor/browser
            on the native app so this escapes to Safari instead of the
            app's own WKWebView, which a plain href/target="_blank" cannot
            reliably do there (the two links above may have this same gap;
            out of scope here, not touched). */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.041 }} className="mb-4">
          <button
            type="button"
            onClick={() => openExternalLink(SCORECARD_URL)}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 bg-card border border-border rounded-xl hover:bg-secondary/40 transition-colors"
          >
            <BarChart3 className="w-4 h-4 text-accent shrink-0" />
            <span className="text-sm font-semibold text-foreground">Run a Score Card ↗</span>
          </button>
        </motion.div>

        {/* Search Bar */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.045 }} className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, company, email, or phone..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                if (e.target.value) {
                  setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                }
              }}
              className="pl-9 pr-9 h-10 bg-card border-border rounded-xl"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>

        {/* Filter by rep, task: 2026-09-17. Always visible, not tied to
            !searchQuery the way StatSquares below is: filtering by rep is
            orthogonal to a text search, not a summary that stops making
            sense once one is typed. */}
        {reps.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.048 }} className="mb-4">
            <Select
              value={repFilter || ALL_REPS}
              onValueChange={(v) => setRepFilter(v === ALL_REPS ? '' : v)}
            >
              <SelectTrigger className="h-10 bg-card border-border rounded-xl">
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                  <SelectValue placeholder="All reps" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_REPS}>All reps</SelectItem>
                {reps.map(rep => (
                  <SelectItem key={rep.id} value={rep.id}>{rep.full_name || rep.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </motion.div>
        )}

        {!searchQuery && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <StatSquares counts={counts} active={filter} onChange={setFilter} />
          </motion.div>
        )}

        {/* Contact List */}
        <div ref={resultsRef} className="space-y-2.5">
          {isLoading || isLoadingShows ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="bg-card rounded-xl p-4 animate-pulse h-16" />
            ))
          ) : filtered.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
              <Handshake className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">No contacts yet</p>
              <p className="text-sm text-muted-foreground/60 mt-1">Scan a badge to get started</p>
            </motion.div>
          ) : (
            filtered.map((meeting, i) => (
              <ContactRow key={meeting.id} meeting={meeting} index={i} />
            ))
          )}
        </div>
      </PageContainer>
    </div>
  );
}
